<script setup>
import { defineProps, onMounted, onUnmounted, ref, watch, defineEmits, computed } from 'vue';
import axios from 'axios';
import InfiniteScroll from '@/components/infinite-scroll.vue';
import RequestDataCard from '@/components/explore/RequestDataCard.vue';
import { useIcons } from '@/composables/icons.js';
import { useRouter } from 'vue-router';
import { useExplorerStore } from '@/store/ExplorerStore';

const explorerStore = useExplorerStore();
const { User } = useIcons();    

const router = useRouter();

const skip = ref(0);

const props = defineProps({

    searchTerm: {
        type: String,
        default: ''
    },
    activeTab: {
        type: String,
        default: ''
    }
});

const listings = ref([]);
const currentPage = ref(1);
const totalCount = ref(0);
const isMobile = ref(window.innerWidth <= 768);
const take = computed(() => isMobile.value ? 8 : 12);

const handleResize = () => {
    isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});
const hasMoreItems = ref(true);
const error = ref(null);
const loading = ref(true);
const deliveryMethods = ref(['dataset', 'rfp', 'eval']);
const selectedType = ref('');
const isInitialMount = ref(true);

const emit = defineEmits(['items-count', 'listings-updated']);


const urlParams = computed(() => {
  const params = { skip: skip.value, take: take.value }
  const { filters, searchQuery } = explorerStore

  if (searchQuery) {
    params.search = searchQuery
  }

  if (props.activeTab && props.activeTab !== 'all') {
    params.type = props.activeTab
  }

  for (const key in filters) {
    const value = filters[key]
    if (Array.isArray(value) && value.length > 0) {
      params[key] = value.join(',')
    } else if (typeof value === 'string' && value !== '') {
      params[key] = value
    }
  }

  return params
})

let lastParams = ''

watch(urlParams, (newVal) => {
  const serialized = JSON.stringify(newVal)
  if (serialized !== lastParams) {
    lastParams = serialized
    router.push({ query: newVal })
    getListings()
  }
}, { deep: true })

watch(
  [() => explorerStore.filters, () => explorerStore.searchQuery, () => props.activeTab],
  () => {
    skip.value = 0
    currentPage.value = 1
    hasMoreItems.value = true
  },
  { deep: true }
)

watch(take, () => {
  skip.value = 0
  currentPage.value = 1
  hasMoreItems.value = true
})

async function getListings() {
  try {
    loading.value = true;
    const config = { params: urlParams.value }
    const response = await axios.get('/api/explore', config)

    updateTotalCountFromResponse(response)
    handleListingsResponse(response)
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false;
  }
}

function updateTotalCountFromResponse(response) {
  if (response.data.count === undefined) {
    return
  }

  totalCount.value = Number(response.data.count) || 0
  emit('items-count', totalCount.value);
}

function handleListingsResponse(response) {
  const newItems = buildNewItemsFromResponse(response)

  if (!newItems.length) {
    return
  }

  if (skip.value === 0) {
    listings.value = newItems
  } else {
    listings.value = [...listings.value, ...newItems]
  }

  emit('listings-updated', listings.value);
  updateHasMoreItems(newItems)
}

function buildNewItemsFromResponse(response) {
  const listingsData = response.data.listings

  if (!Array.isArray(listingsData)) {
    return []
  }

  let newItems = listingsData.map(mapListing)
  const requestedType = urlParams.value.type

  if (isSameTypeAsActiveTab(requestedType)) {
    newItems = applyActiveTabOverrides(newItems)
  } else {
    newItems = applyTabFilters(newItems)
  }

  return newItems
}

function isSameTypeAsActiveTab(requestedType) {
  return Boolean(requestedType && requestedType === props.activeTab)
}

function applyActiveTabOverrides(items) {
  if (!props.activeTab) {
    return items
  }

  const isEvalTab = props.activeTab === 'eval'

  return items.map(item => ({
    ...item,
    delivery_method: props.activeTab,
    ...(isEvalTab ? { price: item.eval_price ?? item.price ?? '0' } : {})
  }))
}

function applyTabFilters(items) {
  const tabFilters = {
    eval: item => item.delivery_method === 'eval',
    rfp: item => item.delivery_method === 'rfp'
  }

  const activeFilter = tabFilters[props.activeTab]

  if (!activeFilter) {
    return items
  }

  return items.filter(activeFilter)
}

function updateHasMoreItems(newItems) {
  if (typeof totalCount.value === 'number' && totalCount.value > 0) {
    hasMoreItems.value = skip.value + take.value < totalCount.value
    return
  }

  hasMoreItems.value = newItems.length === take.value
}

function mapListing(listing) {
  const deliveryMethod = getDeliveryMethod(listing);
  const price = deliveryMethod === 'eval' ? listing.eval_price : (listing.price || '0');
  const sellerWhitelist = listing.datasets?.[0]?.seller?.seller_whitelist === true;
  const hasSeller = Boolean(listing.datasets?.[0]?.seller?.id || listing.datasets?.[0]?.seller?.name);
  
  return {
    ...listing,
    id: listing.id,
    name: listing.name,
    description: listing.short_description,
    image: listing.preview_image,
    price: price,
    is_open_source: listing.is_open_source === true,
    owner: {
      name: listing.applicant?.username || listing.datasets?.[0]?.seller?.name,
      picture: listing.applicant?.profile_pic || listing.datasets?.[0]?.seller?.profile_picture,
      id: listing.applicant?.id || listing.datasets?.[0]?.seller?.id,
      verified: sellerWhitelist,
      communityCreator: !sellerWhitelist && hasSeller,
    },
    tags: setListingTags(listing),
    delivery_method: deliveryMethod
  }
}

const setListingTags = (listing) => {
    const filterTags = listing.tags?.filter(tag => tag?.type !== 'Modality')
    return Array.isArray(filterTags) ? filterTags.slice(0, 3) : [];
}

const getDeliveryMethod = (listing) => {
    if (listing.type === 'rfp') {
        return 'rfp';
    } else if (listing.type === 'eval') {
        return 'eval';
    } else {
        return listing.delivery_method;
    }
}

function formatPrice(price, deliveryMethod = null) {
    const numPrice = Number(price);
    let formattedPrice;
   
    if (Number.isInteger(numPrice)) {
        formattedPrice = '$' + numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } else {
        formattedPrice = '$' + numPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    if (deliveryMethod === 'eval') {
        formattedPrice += '/run';
    }

    return formattedPrice;
}

function getTagStyle(tag) {
    return {
        backgroundColor: `${tag?.display_color}`,
        color: '#000000',
    };
}

function getLongestTag(tags) {
    if (!Array.isArray(tags) || tags.length === 0) return null;
    
    const sortedTags = [...tags].sort((a, b) => (b?.name?.length || 0) - (a?.name?.length || 0));
    return sortedTags[0];
}

function getShorterTags(tags) {
    if (!Array.isArray(tags) || tags.length === 0) return [];
    
    const sortedTags = [...tags].sort((a, b) => (a?.name?.length || 0) - (b?.name?.length || 0));
    
    const longestTag = getLongestTag(tags);
    const remainingTags = sortedTags.filter(tag => tag?.id !== longestTag?.id);
    
    return remainingTags.slice(0, 2);
}

function shouldUseSingleRow(tags) {
    if (!Array.isArray(tags) || tags.length === 0) return false;
    
    if (tags.length === 1) return true;
    
    if (tags.length === 2) return true;
    
    if (tags.length === 3) {
        const totalLength = tags.reduce((sum, tag) => sum + (tag?.name?.length || 0), 0);
        return totalLength <= 20;
    }
    
    return false;
}

function getAllTagsForSingleRow(tags) {
    if (!Array.isArray(tags) || tags.length === 0) return [];
    
    return [...tags].sort((a, b) => (a?.name?.length || 0) - (b?.name?.length || 0));
}

const loadMore = async () => {
    if (loading.value || !hasMoreItems.value) return
    currentPage.value++
    skip.value += take.value
}

function setDeliveryMethod(method) {
    if (selectedType.value !== method) {
        selectedType.value = method;
        currentPage.value = 1;
        listings.value = [];
        hasMoreItems.value = true;
    }
}

const hoverBadgeText = ( listing ) => {
    if (listing.type === 'rfp') {
        return 'Details';
    } else if (listing.price === '0.00' && !listing.is_open_source) {
        return 'Place Bid';
    } 
    else if (listing.is_open_source) {
        return 'View Details';
    }
    else {
        return 'Buy Now';
    }
}

onMounted(async () => {
    isInitialMount.value = false;
    await getListings();
});
</script>

<template>
    <div class="main-container">
        <div v-if="loading && currentPage === 1" class="flex h-full justify-center items-center">
            <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-echoes-primary"></div>
        </div>
        <div v-else-if="error" class="text-center text-red-600">
            {{ error }}
        </div>
        <div v-else class="scroll-container">
            <InfiniteScroll 
                :load-more="loadMore" 
                :loading="loading"
                :has-more="hasMoreItems"
                :has-items="listings.length > 0"
                scroll-container=".scroll-container"
               
            >
                <div class="hidden top-[80px] z-10 py-3 px-6 bg-white dark:bg-slate-900 rounded-xl shadow dark:shadow-gray-700 mb-6">
                    <div class="flex justify-center gap-2">
                        <button
                            v-for="method in deliveryMethods"
                            :key="method"
                            @click="setDeliveryMethod(method)"
                            class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
                            :class="[
                                selectedType === method
                                    ? 'bg-echoes-primary text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-gray-300 dark:hover:bg-slate-700'
                            ]"
                        >
                            {{ method.charAt(0).toUpperCase() + method.slice(1) }}
                        </button>
                    </div>
                </div>
                
                <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-2  xl:gap-4 mt-2 h-full">
                    <template v-for="(listing) in listings" :key="listing.id">
                        <div class="group relative rounded-xl sm:rounded-xl bg-echoes-background-black border transition-all duration-200 h-auto sm:h-[320px] overflow-hidden"
                            :class="listing.premium ? 'border-echoes-border-gray premium-card sm:hover:border-[#ffcc65] sm:hover:shadow-[#ffcc65]/30' : 'border-echoes-border-gray sm:hover:border-echoes-primary sm:hover:shadow-echoes-primary/30'">
                            <router-link 
                                :to="{name: 'item-detail', params: {id: listing.id}}" 
                                class="h-full block"
                            >
                            <div class="h-full flex flex-col">
                            <div class="relative overflow-hidden sm:rounded-t-[12px] h-full sm:h-[200px] image-container">
                                <div class="absolute left-1 z-10">
                                    <span class="px-2 py-0.5 text-[10px] sm:text-sm font-semibold rounded-full bg-[#232323]/80 text-white uppercase">
                                        {{ listing.delivery_method }}
                                    </span>
                                </div>
                                <div v-if="listing.premium" class="relative flex flex-row-reverse right-1 top-1 z-10">
                                    <span class="px-2 py-0.5 flex flex-row-reverse items-center gap-1 text-xs font-semibold rounded-full bg-[#ffcc65] text-black uppercase premium-badge">
                                        <div class="premium-badge-glow"></div>
                                        Premium
                                        <i class="mdi mdi-crown"></i>
                                    </span>
                                </div>
                                <img 
                                    v-if="listing.image"
                                    :src="listing.image" 
                                    :alt="listing.name"
                                    class="w-full h-full sm:h-[200px] object-cover shadow-md dark:shadow-gray-700 border-none transition-all duration-500"
                                >
                                <div v-else class="no-image w-full h-full sm:h-[200px] bg-gray-800 flex items-center justify-center sm:group-hover:scale-110 transition-all duration-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div class="absolute inset-0 bg-black bg-opacity-80 backdrop-blur-sm opacity-0 sm:group-hover:opacity-100 transition-all duration-200 flex flex-col items-center justify-start z-20">
                                    <p v-if="listing.short_description" class="text-xs  font-medium text-gray-200 px-2 pt-2 w-full custom-line-clamp overflow-hidden break-words">{{ listing.short_description }}</p>
                                    <router-link 
                                        :to="{name: 'item-detail', params: {id: listing.id}}"
                                        class="px-3 py-1 bg-echoes-primary hover:bg-echoes-primary/50 text-white/80 hover:text-white font-semibold rounded-full transition-colors duration-200 inline-flex items-center gap-1.5"
                                        :class="{ 'mt-auto mb-auto': !listing.short_description, 'mt-auto mb-3': listing.short_description }"
                                    >
                                        <i v-if="listing.type === 'rfp' && listing.price !== '0.00'" class="uil uil-file text-sm"></i>
                                        <i v-else-if="listing.price !== '0.00'" class="uil uil-bolt-alt text-sm"></i> 
                                        <i v-else-if="listing.price === '0.00' && !listing.is_open_source" class="mdi mdi-gavel text-sm"></i>
                                        <i v-else-if="listing.is_open_source" class="mdi mdi-share text-sm"></i>
                                        <p class="text-xs">{{hoverBadgeText(listing) }}</p>
                                    </router-link>
                                </div>
                                <div class="absolute bottom-1 left-1 right-1">
                                    <div class="flex flex-col gap-1 justify-end items-end overflow-hidden">
                                        
                                        <template v-if="shouldUseSingleRow(listing.tags)">
                                            <div class="flex gap-1 justify-end w-full">
                                                <span 
                                                    v-for="tag in getAllTagsForSingleRow(listing.tags)" 
                                                    :key="`tag-${tag?.id}-single`"
                                                    class="flex items-center px-2 h-4 2xs:h-5 py-0.5 text-[9px] 2xs:text-xs font-bold rounded-full z-10 w-fit min-w-0 max-w-full truncate"
                                                    :style="getTagStyle(tag)"
                                                >
                                                    <span class="truncate text-[#000000]">{{ tag?.name }}</span>
                                                </span>
                                                <span 
                                                    v-if="listing.tags.length > 3"
                                                    class="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full z-10 bg-gray-700/80 text-white shrink-0"
                                                >
                                                    ...
                                                </span>
                                            </div>
                                        </template>
                                        
                                        <template v-else>
                                            <div class="flex gap-1 justify-end w-full">
                                                <span 
                                                    v-if="getLongestTag(listing.tags)"
                                                    :key="`tag-${getLongestTag(listing.tags)?.id}-row2`"
                                                    class="flex items-center px-2 h-4 2xs:h-5 py-0.5 text-[9px] 2xs:text-xs font-bold rounded-full z-10 w-fit min-w-0 max-w-full truncate"
                                                    :style="getTagStyle(getLongestTag(listing.tags))"
                                                >
                                                    <span class="truncate text-[#000000]">{{ getLongestTag(listing.tags)?.name }}</span>
                                                </span>
                                                <span 
                                                    v-if="listing.tags.length > 3"
                                                    class="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full z-10 bg-gray-700/80 text-white shrink-0"
                                                >
                                                    ...
                                                </span>
                                            </div>
                                            <div class="flex gap-1 justify-end w-full">
                                                <span 
                                                    v-for="tag in getShorterTags(listing.tags)" 
                                                    :key="`tag-${tag?.id}-row1`"
                                                    class="flex items-center px-2 h-4 2xs:h-5 py-0.5 text-[9px] 2xs:text-xs font-bold rounded-full z-10 w-fit min-w-0 max-w-full truncate"
                                                    :style="getTagStyle(tag)"
                                                >
                                                    <span class="truncate text-[#000000]">{{ tag?.name }}</span>
                                                </span>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                
                            </div>

                            <!-- Mobile info section below image -->
                            <div class="sm:hidden p-1.5">
                                <h3 class="text-[11px] font-semibold text-white truncate">{{ listing.name }}</h3>
                                <div class="flex flex-col gap-1">
                                    <p class="text-[10px] text-white/80 truncate">{{ listing.owner.name }}</p>
                                    <div class="flex justify-end">
                                        <template v-if="listing.is_open_source">
                                            <span class="text-xs font-semibold text-echoes-primary">Open Source</span>
                                        </template>
                                        <template v-else-if="Number(listing.minimum_bid) > 0 && (listing.price === null || listing.price === '0.00')">
                                            <span class="px-1.5 text-xs font-semibold rounded-full bg-echoes-purple/60 text-echoes-primary">
                                                Bids Only
                                            </span>
                                        </template>
                                        <template v-else>
                                            <span class="text-xs font-semibold text-echoes-primary">{{ formatPrice(listing.price, listing.delivery_method) }}</span>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <div class="px-2 sm:px-2.5 hidden sm:flex flex-col flex-1 space-y-3 sm:space-y-4">
                                <div class="pt-2 h-[50px]">
                                    <h3 class="text-xs sm:text-sm font-semibold text-white break-words overflow-hidden line-clamp-2 max-h-[44px]">{{ listing.name }}</h3>
                                </div>
                                
                                <div class="flex items-center justify-between py-2 border-t border-white/20">
                                            <div class="flex items-center gap-1 sm:gap-1.5">
                                                <template v-if="listing.owner.picture">
                                                    <img 
                                                        :src="listing.owner.picture" 
                                                        class="rounded-full w-6 h-6 sm:w-8 sm:h-8 object-cover"
                                                        :alt="listing.owner.name"
                                                    >
                                                </template>
                                                <template v-else>
                                                    <div class="rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-echoes-purple/50">
                                                        <User class="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                                                    </div>
                                                </template>
                                                <div>
                                                    <h6 class="text-xs sm:text-sm font-medium text-white truncate max-w-[80px] sm:max-w-full">{{ listing.owner.name }}</h6>
                                                    <p
                                                        v-if="listing.owner.verified"
                                                        class="text-[10px] sm:text-xs text-emerald-400 flex items-center gap-1"
                                                        data-testid="explore-card-verified-badge"
                                                    >
                                                        <i class="uil uil-check-circle"></i>
                                                        Verified
                                                    </p>
                                                    <p
                                                        v-else-if="listing.owner.communityCreator"
                                                        class="text-[10px] sm:text-xs text-sky-400 flex items-center gap-1"
                                                        data-testid="explore-card-community-badge"
                                                    >
                                                        <i class="uil uil-users-alt"></i>
                                                        Community creator
                                                    </p>
                                                    <p v-else class="text-[10px] sm:text-xs text-white/50">Owner</p>
                                                </div>
                                            </div>
                                            <div class="flex flex-col items-end">
                                                <p v-if="!listing.is_open_source && listing.price !== '0.00'" class="text-[10px] sm:text-xs font-medium text-white/50">{{ listing.type === 'rfp' ? 'Amount' : 'Price' }}</p>
                                                <template v-if="listing.is_open_source">
                                                    <p class="text-sm font-semibold text-echoes-primary">Open Source</p>
                                                </template>
                                                <template v-else-if="listing.price === null || listing.price === '0.00'">
                                                    <span class="px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-echoes-purple/50 text-white">
                                                        Bids Only
                                                    </span>
                                                </template>
                                                <template v-else>
                                                    <p class="text-sm font-semibold text-echoes-primary">{{ formatPrice(listing.price, listing.delivery_method) }}</p>
                                                </template>
                                            </div>
                                        </div>
                                </div>
                            </div>
                            </router-link>
                        </div>
                    </template>
                    <div class="hidden sm:block">
                    <RequestDataCard
                     class="hidden sm:flex"
                        :key="`request-card-${listings.length}`"
                    />
                    </div>
                </div>
                <div class="block sm:hidden mt-2">
                <RequestDataCard
                    :key="`request-card-${listings.length}`"
                />
                </div>
                <template #loading>
                    <div class="flex justify-center items-center py-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-echoes-primary"></div>
                    </div>
                </template>

                <template #end>
                </template>

                </InfiniteScroll>
            
        </div>
    </div>
</template>
<style lang="sass" scoped>
.premium-card {
    overflow: hidden;
    z-index: 1;
}

@media (min-width: 640px) {
  .premium-card:hover {
      overflow: hidden;
      z-index: 1;
      animation: travelHighlight 3s linear infinite;
      box-shadow: 
          0 0 6px rgba(255, 204, 101, 0.4),
          0 0 8px rgba(255, 204, 101, 0.2),
          0 1px 2px rgba(0, 0, 0, 0.1);
  }
}

@keyframes travelHighlight {
    0% {
      background-position: 0 0, 0 0, 0 0, 0 0, 0 -100%;
    }
    100% {
      background-position: 0 0, 0 0, 0 0, 0 0, 0 100%;
    }
}

.premium-badge {
    position: relative;
    display: flex;
    align-items: center;
    color: #000000;
    background-size: 250%;
    background: #ffcc65;
    background: linear-gradient(to right,#77530a,#ffcc65,#CCA341,#CCA341,#ffcc65,#77530a);
    border-radius: 10px;
    overflow: hidden;
    z-index: 1;
    box-shadow: 
        0 0 6px rgba(255, 204, 101, 0.4),
        0 0 12px rgba(255, 204, 101, 0.2),
        0 2px 4px rgba(0, 0, 0, 0.1);
}

.premium-badge::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(0deg);
    width: 200px;
    height: 200px;
    background: conic-gradient(
        rgba(0,0,0,0),
        #ffcc65,
        #fff8dc,
        #ffcc65,
        rgba(0,0,0,0) 30%
    );
    animation: borderFlare 3s linear infinite;
    z-index: -1;
    filter: blur(1px);
}

.premium-badge::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    right: 1px;
    bottom: 1px;
    background: linear-gradient(to right,#77530a,#ffcc65,#CCA341,#CCA341,#ffcc65,#77530a);
    border-radius: 10px;
    z-index: -1;
}

@keyframes borderFlare {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.premium-badge-glow {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 10px;
    overflow: hidden;
    z-index: -3;
}

.premium-badge-glow::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(0deg);
    width: 180px;
    height: 180px;
    background: conic-gradient(
        rgba(0,0,0,0), 
        rgba(255, 204, 101, 0.6), 
        rgba(255, 248, 220, 0.8),
        rgba(255, 204, 101, 0.6),
        rgba(0,0,0,0) 25%
    );
    animation: borderFlare 5s linear infinite reverse;
    filter: blur(8px);
}

.custom-line-clamp {
    overflow: hidden !important;
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    line-clamp: 6 !important;
}


.image-container::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to top, rgba(0,0,0,0.25) 50%, transparent 100%);
    pointer-events: none;
    z-index: 1;
}

  @media (max-width: 639px) {
    .image-container {
        aspect-ratio: 1 / 1;
        height: auto;
    }
    .image-container img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .no-image {
        aspect-ratio: 1 / 1;
    }
  }

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.main-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.scroll-container {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.infinite-scroll {
    overflow: visible;
}


</style>